import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive, ArchiveRestore, ArrowDownCircle, ArrowUpCircle, ChefHat, CheckCircle2, Edit,
  History, Link2, Lock, Plus, RotateCcw, Search, Trash2, Wallet, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { NumberInput } from '@/components/ui/NumberInput';
import {
  createStock, deleteStock, listStock, updateStock,
  createStockMovement, resetStock,
  listTechCards, saveDishTechCard, listPreps, setStockItemKind,
  listInventoryActs, createInventoryAct, commitInventoryAct, deleteInventoryAct,
  listSupplies, countArchivedSupplies, createSupply, updateSupply, deleteSupply,
  archiveSupply, unarchiveSupply, getCurrentShift,
  listDishes,
  WRITE_OFF_REASON_LABELS,
  type StockItem, type TechCard, type WriteOffReason,
  type Supply, type SupplyItem, type SupplyPaymentSource,
} from '@/services/admin';
import { TechCardEditor, PrepRecipeModal } from '@/components/techcard/TechCardEditor';
import { useAuthStore } from '@/store/auth.store';
import { StockAnalyticsTab } from './warehouse/StockAnalyticsTab';
import { formatDate, formatRub } from '@/utils/format';
import { CANONICAL_UNITS } from '@/utils/units';
import {
  baseToPacks, fmtQty, hasPackaging, packagingCount, packagingLabel, packagingMath,
  packsToBase, roundQty, shortPack,
} from '@/utils/packaging';

type Tab = 'items' | 'analytics' | 'tech-cards' | 'preps' | 'inventory' | 'supplies';

const paymentSourceLabels: Record<SupplyPaymentSource, string> = {
  NONE: 'Без оплаты',
  KASSA_CASH: 'Наличные из кассы',
  KASSA_TRANSFER: 'Перевод из кассы',
  EXTERNAL: 'Внешняя оплата',
};
// Доступные при создании источники оплаты (перевод из кассы временно убран).
const SUPPLY_PAYMENT_OPTIONS: SupplyPaymentSource[] = ['NONE', 'KASSA_CASH', 'EXTERNAL'];
const paymentSourceTone: Record<SupplyPaymentSource, 'success' | 'warning' | 'info' | 'slate'> = {
  NONE: 'slate',
  KASSA_CASH: 'success',
  KASSA_TRANSFER: 'info',
  EXTERNAL: 'warning',
};

export default function WarehousePage() {
  const [tab, setTab] = useState<Tab>('items');

  return (
    <div>
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">Склад</h1>
          <p className="text-sm text-slate-500">Остатки, аналитика движений, тех-карты, заготовки, инвентаризация</p>
        </div>
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg overflow-x-auto no-scrollbar w-full sm:w-auto">
          <TabBtn active={tab === 'items'} onClick={() => setTab('items')}>
            Позиции
          </TabBtn>
          <TabBtn active={tab === 'analytics'} onClick={() => setTab('analytics')}>
            Аналитика
          </TabBtn>
          <TabBtn active={tab === 'supplies'} onClick={() => setTab('supplies')}>
            Поставки
          </TabBtn>
          <TabBtn active={tab === 'tech-cards'} onClick={() => setTab('tech-cards')}>
            Тех-карты
          </TabBtn>
          <TabBtn active={tab === 'preps'} onClick={() => setTab('preps')}>
            Заготовки
          </TabBtn>
          <TabBtn active={tab === 'inventory'} onClick={() => setTab('inventory')}>
            Инвентаризация
          </TabBtn>
        </div>
      </header>

      {tab === 'items' && <ItemsTab />}
      {tab === 'analytics' && <StockAnalyticsTab />}
      {tab === 'supplies' && <SuppliesTab />}
      {tab === 'tech-cards' && <TechCardsTab />}
      {tab === 'preps' && <PrepsTab />}
      {tab === 'inventory' && <InventoryTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap shrink-0 ${active ? 'bg-white shadow text-brand-700' : 'text-slate-600'}`}
    >
      {children}
    </button>
  );
}

// ─── Позиции ──────────────────────────────────────────────────────────
// Количество и цену вводят в ОДНОЙ выбранной единице: либо в базовой (unit),
// либо в фасовке («упаковка по 1 л»). Выбор единицы — тот же список, что и
// фасовка, поэтому отдельного переключателя режима нет. В API уходят базовые
// единицы: тех-карты и списания считают только в них.
type ItemForm = {
  id?: string;
  name: string;
  unit: string;
  packLabel: string; // '' = вводим в базовой единице, фасовки нет
  packSize: number;
  quantity: number; // в выбранной единице; при правке позиции не отправляется
  minQuantity: number; // всегда в базовой единице — это порог остатка
  priceRub: number; // за выбранную единицу
  currentQty: number; // остаток как есть, только для показа при правке
};

const PACK_PRESETS = [
  'упаковка', 'пачка', 'бутылка', 'банка', 'ящик', 'коробка', 'мешок', 'канистра', 'рулон',
];
// Канон + пара специфичных. Поле остаётся свободным (у кого-то «ящик»), но
// подсказки первыми показывают нормальные варианты: на проде рядом жили «кг»,
// «Кг», «грамм» и «упаковкка», и для системы это были разные единицы.
const UNIT_SUGGESTIONS = [...CANONICAL_UNITS, 'м', 'бут'];

function pluralIngredients(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'ингредиент';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'ингредиента';
  return 'ингредиентов';
}

const emptyItemForm = (): ItemForm => ({
  name: '', unit: 'шт', packLabel: '', packSize: 0,
  quantity: 0, minQuantity: 0, priceRub: 0, currentQty: 0,
});

function ItemsTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [lowOnly, setLowOnly] = useState(false);
  const [form, setForm] = useState<ItemForm | null>(null);
  const [moveFor, setMoveFor] = useState<StockItem | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const isOwner = useAuthStore((s) => s.user?.role === 'OWNER');

  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/items', search, lowOnly],
    queryFn: () => listStock({ q: search || undefined, lowOnly: lowOnly || undefined }),
  });

  // Фасовка живёт только парой «название + размер»: выбрали упаковку, но не
  // указали объём — считаем, что фасовки нет, и работаем в базовой единице.
  const packed = !!form && form.packLabel.trim().length > 0 && form.packSize > 0;
  const factor = packed && form ? form.packSize : 1;
  const unitName = form ? form.unit.trim() || 'ед.' : 'ед.';
  const inputUnit = packed && form ? form.packLabel.trim() : unitName;
  const baseQty = form ? roundQty(form.quantity * factor) : 0;
  const baseCostKop = form ? Math.round((form.priceRub * 100) / factor) : 0;

  const saveMut = useMutation({
    mutationFn: async () => {
      if (!form) return;
      const payload = {
        name: form.name.trim(),
        unit: form.unit.trim() || 'шт',
        minQuantity: form.minQuantity,
        costPerUnit: baseCostKop,
        packLabel: packed ? form.packLabel.trim() : null,
        packSize: packed ? form.packSize : null,
      };
      // Остаток задаётся только при создании. У существующей позиции его меняют
      // приходом/расходом или инвентаризацией — иначе сохранение карточки молча
      // перезаписывало бы склад мимо движений.
      if (form.id) return updateStock(form.id, payload);
      return createStock({ ...payload, quantity: baseQty });
    },
    onSuccess: () => {
      toast.success('Сохранено');
      setForm(null);
      qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteStock,
    onSuccess: () => {
      toast.success('Удалено');
      qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    },
  });

  const openCreate = () => setForm(emptyItemForm());
  // Цену показываем за ту же единицу, в которой позицию закупают: есть фасовка —
  // за упаковку, нет — за базовую единицу.
  const openEdit = (s: StockItem) => {
    const k = s.packSize && s.packSize > 0 && s.packLabel ? s.packSize : 1;
    setForm({
      id: s.id, name: s.name, unit: s.unit,
      packLabel: s.packLabel ?? '', packSize: s.packSize ?? 0,
      quantity: 0, minQuantity: s.minQuantity,
      priceRub: roundQty((s.costPerUnit / 100) * k), currentQty: s.quantity,
    });
  };

  // Смена единицы = смена фасовки: базовая единица очищает её, упаковка —
  // задаёт название и оставляет размер для ввода.
  const pickUnit = (value: string) => {
    if (!form) return;
    if (value === '') setForm({ ...form, packLabel: '', packSize: 0 });
    else setForm({ ...form, packLabel: value, packSize: form.packSize || 1 });
  };

  // Выбрана упаковка без объёма — не сохраняем молча в базовых единицах, а просим
  // дозаполнить: иначе «10 упаковок» уехали бы на склад как 10 литров.
  const packSizeMissing = !!form && form.packLabel.trim().length > 0 && !(form.packSize > 0);
  const canSubmit = (form?.name?.trim().length ?? 0) > 0 && !packSizeMissing;

  return (
    <>
      <div className="card p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="label">Поиск</label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="input pl-9"
              placeholder="Название или артикул"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />
          Только с низким остатком
        </label>
        {/* Сброс всех остатков — только владельцу: сервер всё равно откажет остальным. */}
        {isOwner && (
          <button type="button" className="btn-danger ml-auto" onClick={() => setResetOpen(true)}>
            <RotateCcw size={16} /> Сбросить остатки
          </button>
        )}
        <button className={`btn-primary ${isOwner ? '' : 'ml-auto'}`} onClick={openCreate}>
          <Plus size={16} /> Позиция
        </button>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Название</th>
                <th className="text-right px-4 py-3">Остаток</th>
                <th className="text-right px-4 py-3">Минимум</th>
                <th className="text-right px-4 py-3">Цена/ед.</th>
                <th className="text-right px-4 py-3">Стоимость</th>
                <th className="text-right px-4 py-3">В меню</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => {
                const low = s.quantity <= s.minQuantity;
                return (
                  <tr key={s.id} className="table-row">
                    <td className="px-4 py-3">
                      <div className="font-medium flex flex-wrap items-center gap-2">
                        {s.name}
                        {/* У заготовки цена берётся из рецепта — не путать с закупкой. */}
                        {s.kind === 'PREP' && (
                          <span className="text-[10px] uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
                            заготовка
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400">
                        {s.unit}
                        {packagingLabel(s) && <> · {packagingLabel(s)}</>}
                        {s.kind === 'PREP' && (
                          <> · {s.recipeItems
                            ? `состав: ${s.recipeItems} ${pluralIngredients(s.recipeItems)}`
                            : 'состав не заполнен'}</>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={low ? 'text-rose-600 font-semibold' : ''}>{fmtQty(s.quantity)}</span>
                      {low && <span className="ml-2"><Badge tone="danger">Низкий</Badge></span>}
                      {hasPackaging(s) && (
                        <div className="text-xs text-slate-400">{packagingCount(s.quantity, s)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">{s.minQuantity}</td>
                    <td className="px-4 py-3 text-right">{formatRub(s.costPerUnit)}</td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatRub(Math.round(s.quantity * s.costPerUnit))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Badge tone="slate">{s.linkedDishes ?? 0}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button className="btn-ghost" title="Приход/Расход" onClick={() => setMoveFor(s)}>
                        <History size={16} />
                      </button>
                      <button className="btn-ghost" onClick={() => openEdit(s)}><Edit size={16} /></button>
                      <button
                        className="btn-ghost text-red-600"
                        onClick={() => { if (confirm(`Удалить «${s.name}»?`)) delMut.mutate(s.id); }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {data?.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Нет позиций</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={!!form}
        onClose={() => setForm(null)}
        title={form?.id ? 'Редактировать позицию' : 'Новая позиция'}
        footer={
          <>
            <button className="btn-outline" onClick={() => setForm(null)}>Отмена</button>
            <button
              className="btn-primary"
              disabled={!canSubmit || saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              Сохранить
            </button>
          </>
        }
      >
        {form && (
          <div className="grid gap-4">
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                placeholder="Молоко 3,2%"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Ед. учёта</label>
                <input
                  className="input"
                  list="stock-units"
                  placeholder="л"
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                />
                <datalist id="stock-units">
                  {UNIT_SUGGESTIONS.map((u) => <option key={u} value={u} />)}
                </datalist>
              </div>
              <div>
                <label className="label">Мин. остаток, {unitName}</label>
                <NumberInput className="input tabular-nums" value={form.minQuantity}
                  onChange={(n) => setForm({ ...form, minQuantity: n })} />
              </div>
            </div>

            {/* Количество вводят так, как товар привозят: «10 упаковок по 1 л». */}
            <div className="grid gap-2">
              <label className="label">
                {form.id ? 'Как закупается' : 'Сколько добавить на склад'}
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {!form.id && (
                  <NumberInput
                    className="input w-20 text-right tabular-nums"
                    value={form.quantity}
                    onChange={(n) => setForm({ ...form, quantity: n })}
                  />
                )}
                <select
                  className="input flex-1"
                  value={form.packLabel}
                  onChange={(e) => pickUnit(e.target.value)}
                >
                  <option value="">{unitName} — без фасовки</option>
                  {[...new Set([...PACK_PRESETS, ...(form.packLabel ? [form.packLabel] : [])])].map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {!!form.packLabel && (
                  <>
                    <span className="text-sm text-slate-500 shrink-0">по</span>
                    <NumberInput
                      className="input w-20 text-right tabular-nums"
                      value={form.packSize}
                      onChange={(n) => setForm({ ...form, packSize: n })}
                    />
                    <span className="text-sm text-slate-500 shrink-0">{unitName}</span>
                  </>
                )}
              </div>
              {packSizeMissing && (
                <div className="text-xs text-rose-600">
                  Укажите, сколько {unitName} содержит 1 {form.packLabel.trim()}.
                </div>
              )}
            </div>

            <div>
              {/* Склонять пользовательское слово нечем, поэтому «₽ / упаковка». */}
              <label className="label">Цена, ₽ / {inputUnit}</label>
              <NumberInput className="input tabular-nums" value={form.priceRub}
                onChange={(n) => setForm({ ...form, priceRub: n })} />
            </div>

            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 grid gap-1">
              {form.id ? (
                <span>
                  Остаток сейчас: <b>{fmtQty(form.currentQty)} {unitName}</b>
                  {packed && <> ({packagingCount(form.currentQty, form)})</>}
                  {' — меняется приходом, расходом и инвентаризацией.'}
                </span>
              ) : (
                <span>
                  На склад встанет{' '}
                  <b>
                    {packed
                      ? packagingMath(form.quantity, { ...form, unit: unitName })
                      : `${fmtQty(form.quantity)} ${unitName}`}
                  </b>
                </span>
              )}
              {packed && form.priceRub > 0 && (
                <span>Себестоимость: <b>{formatRub(baseCostKop)}</b> за 1 {unitName}</span>
              )}
            </div>
          </div>
        )}
      </Modal>

      {moveFor && <QuickMovementModal item={moveFor} onClose={() => setMoveFor(null)} />}
      {resetOpen && <ResetStockModal onClose={() => setResetOpen(false)} />}
    </>
  );
}

function QuickMovementModal({ item, onClose }: { item: StockItem; onClose: () => void }) {
  const qc = useQueryClient();
  const packed = hasPackaging(item);
  const [type, setType] = useState<'ARRIVAL' | 'CONSUMPTION' | 'WRITE_OFF'>('ARRIVAL');
  // Приход почти всегда привозят упаковками, расход и списание считают в базовой
  // единице — с этого и начинаем, переключатель рядом.
  const [inPacks, setInPacks] = useState(packed);
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState(
    packed ? String(roundQty((item.costPerUnit / 100) * (item.packSize ?? 1))) : (item.costPerUnit / 100).toString(),
  );
  const [comment, setComment] = useState('');
  const [reason, setReason] = useState<WriteOffReason>('SPOILAGE');

  const packSize = item.packSize ?? 1;
  const factor = inPacks && packed ? packSize : 1;

  // Смена единицы пересчитывает уже введённое, чтобы смысл не поехал:
  // 5 упаковок по 2 л — это 10 л, а 90 ₽ за упаковку — 45 ₽/л.
  const switchInputUnit = (toPacks: boolean) => {
    if (!packed || toPacks === inPacks) return;
    const q = Number(quantity);
    const p = Number(price);
    if (Number.isFinite(q)) {
      setQuantity(String(toPacks ? baseToPacks(q, packSize) : packsToBase(q, packSize)));
    }
    if (Number.isFinite(p)) {
      setPrice(String(toPacks ? roundQty(p * packSize) : roundQty(p / packSize)));
    }
    setInPacks(toPacks);
  };

  const mut = useMutation({
    mutationFn: () => createStockMovement({
      stockItemId: item.id,
      type,
      quantity: roundQty(Number(quantity) * factor),
      costPerUnit: type === 'ARRIVAL' && price ? Math.round((Number(price) * 100) / factor) : undefined,
      comment: comment.trim() || undefined,
      reason: type === 'WRITE_OFF' ? reason : undefined,
    }),
    onSuccess: () => {
      toast.success('Движение записано');
      qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/movements'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/analytics'] });
      onClose();
    },
  });

  const q = Number(quantity);
  const canSubmit = Number.isFinite(q) && q > 0;
  const baseQ = canSubmit ? roundQty(q * factor) : 0;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Движение: ${item.name}`}
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
            Провести
          </button>
        </>
      }
    >
      <div className="grid gap-3">
        <div>
          <label className="label">Тип операции</label>
          <div className="flex flex-wrap gap-2">
            <OpBtn active={type === 'ARRIVAL'} onClick={() => setType('ARRIVAL')} color="success">
              <ArrowDownCircle size={16} /> Приход
            </OpBtn>
            <OpBtn active={type === 'CONSUMPTION'} onClick={() => setType('CONSUMPTION')} color="warning">
              <ArrowUpCircle size={16} /> Расход
            </OpBtn>
            <OpBtn active={type === 'WRITE_OFF'} onClick={() => setType('WRITE_OFF')} color="danger">
              <Trash2 size={16} /> Списание
            </OpBtn>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Количество</label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                className="input flex-1 min-w-0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
              {packed ? (
                <select
                  className="input w-28 shrink-0"
                  value={inPacks ? 'pack' : 'base'}
                  onChange={(e) => switchInputUnit(e.target.value === 'pack')}
                >
                  <option value="pack">{item.packLabel}</option>
                  <option value="base">{item.unit}</option>
                </select>
              ) : (
                <span className="self-center text-sm text-slate-500 w-14 shrink-0">{item.unit}</span>
              )}
            </div>
          </div>
          {type === 'ARRIVAL' && (
            <div>
              <label className="label">
                Цена, ₽ / {inPacks && packed ? item.packLabel : item.unit}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="input"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          )}
        </div>
        {type === 'WRITE_OFF' && (
          <div>
            <label className="label">Причина</label>
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(WRITE_OFF_REASON_LABELS) as WriteOffReason[]).map((r) => (
                <OpBtn key={r} active={reason === r} onClick={() => setReason(r)} color="danger">
                  {WRITE_OFF_REASON_LABELS[r]}
                </OpBtn>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="label">Комментарий</label>
          <input
            className="input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={type === 'WRITE_OFF' ? 'Что случилось' : 'Необязательно'}
          />
        </div>
        <div className="text-xs text-slate-500">
          {inPacks && packed && canSubmit && (
            <div className="mb-1">
              {packagingMath(q, item)}
              {type === 'ARRIVAL' && Number(price) > 0 && (
                <> · {formatRub(Math.round((Number(price) * 100) / factor))}/{item.unit}</>
              )}
            </div>
          )}
          Текущий остаток: <b>{fmtQty(item.quantity)}</b> {item.unit}
          {canSubmit && (
            <> → после операции:{' '}
              <b>
                {fmtQty(type === 'ARRIVAL' ? item.quantity + baseQ : item.quantity - baseQ)}
              </b>{' '}{item.unit}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

// Сброс остатков всех позиций до нуля. Подтверждение словом: массовое
// обнуление одной кнопкой не откатить — вернуть можно только приходом или
// инвентаризацией. В «Потери» сброс не идёт, в журнале — «Сброс остатка».
const RESET_WORD = 'СБРОС';

function pluralPositions(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'позиция';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'позиции';
  return 'позиций';
}

function ResetStockModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [confirmText, setConfirmText] = useState('');
  // Полный список без поиска и фильтра вкладки — тот же ключ, что у таблицы без фильтров.
  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/items', '', false],
    queryFn: () => listStock({}),
  });
  const nonZero = (data ?? []).filter((s) => s.quantity !== 0);
  const value = nonZero.reduce((sum, s) => sum + (s.quantity > 0 ? s.quantity * s.costPerUnit : 0), 0);
  const negative = nonZero.filter((s) => s.quantity < 0).length;
  const confirmed = confirmText.trim().toUpperCase() === RESET_WORD;

  const mut = useMutation({
    mutationFn: () => resetStock(comment.trim() || undefined),
    onSuccess: (r) => {
      toast.success(
        r.count > 0
          ? `Остатки сброшены до нуля: ${r.count} ${pluralPositions(r.count)}`
          : 'Остатки уже были нулевыми',
      );
      qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/movements'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/analytics'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      size="sm"
      onClose={() => { if (!mut.isPending) onClose(); }}
      title="Сбросить все остатки до нуля?"
      footer={
        !isLoading && nonZero.length === 0 ? (
          <button type="button" className="btn-outline" onClick={onClose}>Закрыть</button>
        ) : (
          <>
            <button type="button" className="btn-outline" onClick={onClose} disabled={mut.isPending}>
              Не сбрасывать
            </button>
            <button
              type="button"
              className="btn-danger disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!confirmed || mut.isPending || isLoading}
              onClick={() => mut.mutate()}
            >
              <RotateCcw size={16} /> {mut.isPending ? 'Сбрасываем…' : 'Сбросить остатки'}
            </button>
          </>
        )
      }
    >
      {isLoading ? (
        <Spinner />
      ) : nonZero.length === 0 ? (
        <p className="text-sm text-slate-600">Остатки всех позиций уже нулевые — сбрасывать нечего.</p>
      ) : (
        <div className="grid gap-4">
          <p className="text-sm text-slate-600">Остаток каждой позиции станет 0.</p>
          <dl className="rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm">
            <div className="flex justify-between gap-3 px-3.5 py-2.5">
              <dt className="text-slate-500">Позиций с остатком</dt>
              <dd className="font-medium tabular-nums">{nonZero.length}</dd>
            </div>
            <div className="flex justify-between gap-3 px-3.5 py-2.5">
              <dt className="text-slate-500">Стоимость остатков</dt>
              <dd className="font-medium tabular-nums">{formatRub(Math.round(value))}</dd>
            </div>
            {negative > 0 && (
              <div className="flex justify-between gap-3 px-3.5 py-2.5">
                <dt className="text-slate-500">Из них в минусе</dt>
                <dd className="font-medium tabular-nums">{negative}</dd>
              </div>
            )}
          </dl>
          <ul className="list-disc pl-5 grid gap-1.5 text-sm text-slate-600">
            <li>По каждой позиции в журнал склада запишется «Сброс остатка» с вашим именем.</li>
            <li>В «Потери» и недостачу сброс не входит.</li>
            <li>
              Вернуть остатки можно только приходом или инвентаризацией. Списания, сделанные до сброса,
              отменить будет нельзя.
            </li>
          </ul>
          <div>
            <label className="label" htmlFor="reset-stock-comment">Причина — необязательно</label>
            <input
              id="reset-stock-comment"
              className="input"
              maxLength={300}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Например: начинаем учёт заново"
            />
          </div>
          <div>
            <label className="label" htmlFor="reset-stock-confirm">Чтобы подтвердить, введите «{RESET_WORD}»</label>
            <input
              id="reset-stock-confirm"
              className="input"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && confirmed && !mut.isPending) mut.mutate(); }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

function OpBtn({ active, onClick, color, children }: {
  active: boolean; onClick: () => void;
  color: 'success' | 'warning' | 'danger';
  children: React.ReactNode;
}) {
  const tones = {
    success: active ? 'bg-emerald-600 text-white' : 'text-emerald-700 border border-emerald-300',
    warning: active ? 'bg-amber-500 text-white' : 'text-amber-700 border border-amber-300',
    danger: active ? 'bg-rose-600 text-white' : 'text-rose-700 border border-rose-300',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${tones[color]}`}
    >
      {children}
    </button>
  );
}

// ─── Тех-карты ────────────────────────────────────────────────────────
// Список блюд с покрытием и фильтрами. Раньше здесь показывались только блюда,
// у которых тех-карта уже есть, — а на проде это 13 блюд из 2278, то есть
// владелец видел почти пустой экран и не понимал, что делать дальше.

type CardFilter = 'all' | 'missing' | 'manual' | 'ready';

function TechCardsTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ id: string; name: string; price: number } | null>(null);
  const [filter, setFilter] = useState<CardFilter>('missing');
  const [q, setQ] = useState('');
  // Очередь заполнения: список id блюд, по которым идём подряд.
  const [queue, setQueue] = useState<string[] | null>(null);

  const { data: cards, isLoading } = useQuery({
    queryKey: ['admin/stock/tech-cards'],
    queryFn: listTechCards,
  });
  const { data: dishes = [] } = useQuery({
    queryKey: ['admin/menu/dishes'],
    queryFn: () => listDishes(),
  });

  const byDish = useMemo(() => {
    const map = new Map<string, TechCard[]>();
    for (const tc of cards ?? []) {
      const list = map.get(tc.dishId) ?? [];
      list.push(tc);
      map.set(tc.dishId, list);
    }
    return map;
  }, [cards]);

  const rows = useMemo(() => {
    const list = dishes.map((d) => {
      const card = byDish.get(d.id) ?? [];
      const costKop = card.reduce(
        (s, tc) => s + Math.round(tc.quantity * (tc.stockItem?.costPerUnit ?? 0)),
        0,
      );
      return {
        dish: d,
        card,
        costKop,
        // Ручная цена перебивает тех-карту в аналитике — это отдельный статус.
        hasManual: d.costPrice != null,
        foodCostPct: d.price > 0 && costKop > 0 ? Math.round((costKop / d.price) * 100) : null,
      };
    });
    const query = q.trim().toLowerCase();
    return list
      .filter((r) => {
        if (filter === 'missing' && r.card.length > 0) return false;
        if (filter === 'ready' && r.card.length === 0) return false;
        if (filter === 'manual' && !r.hasManual) return false;
        return !query || r.dish.name.toLowerCase().includes(query);
      })
      .sort((a, b) => a.dish.name.localeCompare(b.dish.name, 'ru'));
  }, [dishes, byDish, filter, q]);

  const covered = byDish.size;
  const total = dishes.length;
  const coveragePct = total > 0 ? Math.round((covered / total) * 100) : 0;
  const missingIds = useMemo(
    () => dishes.filter((d) => !byDish.has(d.id)).map((d) => d.id),
    [dishes, byDish],
  );

  const openDish = (id: string) => {
    const d = dishes.find((x) => x.id === id);
    if (d) setEditing({ id: d.id, name: d.name, price: d.price });
  };

  // Режим «по очереди»: закрыли одно блюдо — сразу открылось следующее.
  const advanceQueue = () => {
    if (!queue) return;
    const [, ...rest] = queue;
    if (rest.length === 0) {
      setQueue(null);
      setEditing(null);
      toast.success('Очередь пройдена');
      return;
    }
    setQueue(rest);
    openDish(rest[0]);
  };

  return (
    <>
      <div className="card p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-sm">
              Тех-карты есть у <b>{covered}</b> из <b>{total}</b> блюд
              <span className="text-slate-400"> ({coveragePct}%)</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Чем больше блюд с тех-картой, тем точнее себестоимость и прибыль в аналитике.
            </div>
          </div>
          {missingIds.length > 0 && (
            <button
              className="btn-primary"
              onClick={() => { setQueue(missingIds); openDish(missingIds[0]); }}
            >
              <Link2 size={15} /> Заполнить по очереди ({missingIds.length})
            </button>
          )}
        </div>
        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${coveragePct}%` }}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          <FilterBtn active={filter === 'missing'} onClick={() => setFilter('missing')}>
            Без тех-карты
          </FilterBtn>
          <FilterBtn active={filter === 'ready'} onClick={() => setFilter('ready')}>
            С тех-картой
          </FilterBtn>
          <FilterBtn active={filter === 'manual'} onClick={() => setFilter('manual')}>
            С ручной ценой
          </FilterBtn>
          <FilterBtn active={filter === 'all'} onClick={() => setFilter('all')}>
            Все
          </FilterBtn>
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-8"
            placeholder="Поиск блюда…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? <Spinner /> : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Link2 size={24} className="mx-auto mb-2 opacity-40" />
            {filter === 'missing' ? 'У всех блюд есть тех-карта' : 'Ничего не найдено'}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(({ dish, card, costKop, hasManual, foodCostPct }) => (
              <div key={dish.id} className="p-4 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0 basis-full sm:basis-0">
                  <div className="font-medium mb-1 flex flex-wrap items-center gap-2">
                    {dish.name}
                    {card.length === 0 && <Badge tone="slate">нет тех-карты</Badge>}
                    {hasManual && <Badge tone="warning">ручная цена</Badge>}
                  </div>
                  {card.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {card.map((tc) => (
                        <span
                          key={tc.id}
                          className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-600"
                        >
                          {tc.stockItem?.name}{' '}
                          <b className="tabular-nums">
                            {fmtQty(tc.quantity)} {tc.stockItem?.unit}
                          </b>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    {card.length > 0 ? (
                      <>
                        <span className="text-slate-500">
                          Себестоимость: <b className="text-slate-800 tabular-nums">{formatRub(costKop)}</b>
                        </span>
                        {dish.price > 0 && (
                          <span className="text-slate-500">
                            Цена: <b className="tabular-nums">{formatRub(dish.price)}</b>
                          </span>
                        )}
                        {foodCostPct != null && (
                          <Badge tone={foodCostPct <= 35 ? 'success' : foodCostPct <= 60 ? 'warning' : 'danger'}>
                            Фудкост {foodCostPct}%
                          </Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-slate-400 text-xs">
                        {hasManual
                          ? `Себестоимость взята из ручной цены ${formatRub(dish.costPrice ?? 0)}`
                          : 'Себестоимость не считается — соберите состав'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className={card.length > 0 ? 'btn-outline shrink-0' : 'btn-primary shrink-0'}
                  onClick={() => setEditing({ id: dish.id, name: dish.name, price: dish.price })}
                >
                  {card.length > 0 ? <><Edit size={14} /> Изменить</> : <><Plus size={14} /> Собрать</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <TechCardEditor
          dishId={editing.id}
          dishName={editing.name}
          dishPrice={editing.price}
          onClose={() => {
            if (queue) { setQueue(null); }
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin/stock/tech-cards'] });
            if (queue) advanceQueue();
          }}
        />
      )}
    </>
  );
}

function FilterBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs sm:text-sm rounded-md transition-colors whitespace-nowrap ${
        active ? 'bg-white shadow-sm font-medium' : 'text-slate-500 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Заготовки ───────────────────────────────────────────────────────
// Отдельная вкладка: заготовки живут среди позиций склада, но у них своя логика
// (себестоимость из рецепта), и владельцу нужно видеть их списком.

function PrepsTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [converting, setConverting] = useState(false);

  const { data: preps, isLoading } = useQuery({
    queryKey: ['admin/stock/preps'],
    queryFn: listPreps,
  });
  const { data: items = [] } = useQuery({
    queryKey: ['admin/stock/items-all'],
    queryFn: () => listStock(),
  });

  const goods = useMemo(() => items.filter((i) => i.kind !== 'PREP'), [items]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin/stock/preps'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items-all'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
    qc.invalidateQueries({ queryKey: ['admin/stock/tech-cards'] });
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-slate-500">
          Заготовка — то, что готовят у себя: соус, тесто, бульон. Её себестоимость считается
          из состава и обновляется вместе с ценами ингредиентов.
        </div>
        <button className="btn-primary shrink-0" onClick={() => setConverting(true)}>
          <ChefHat size={15} /> Новая заготовка
        </button>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? <Spinner /> : (preps ?? []).length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <ChefHat size={24} className="mx-auto mb-2 opacity-40" />
            Заготовок пока нет
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {(preps ?? []).map((p) => (
              <div key={p.id} className="p-4 flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0 basis-full sm:basis-0">
                  <div className="font-medium mb-1 flex flex-wrap items-center gap-2">
                    {p.name}
                    {p.trackStock
                      ? <Badge tone="info">ведём остаток</Badge>
                      : <Badge tone="slate">списывается сырьём</Badge>}
                    {p.recipe.length === 0 && <Badge tone="warning">состав не заполнен</Badge>}
                  </div>
                  {p.recipe.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {p.recipe.map((r) => (
                        <span
                          key={r.componentId}
                          className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-600"
                        >
                          {r.component.name}{' '}
                          <b className="tabular-nums">{fmtQty(r.quantity)} {r.component.unit}</b>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span>
                      Себестоимость:{' '}
                      <b className="text-slate-800 tabular-nums">{formatRub(p.costPerUnit)}</b>
                      {' '}/ {p.unit}
                    </span>
                    {p.outputQuantity != null && (
                      <span>
                        Выход (нетто):{' '}
                        <b className="tabular-nums">{fmtQty(p.effectiveOutput ?? p.outputQuantity)} {p.unit}</b>
                      </span>
                    )}
                    {(p.wastePercent ?? 0) > 0 && (
                      <span>
                        Потери: <b className="tabular-nums text-amber-700">{fmtQty(p.wastePercent ?? 0)}%</b>
                        {' '}· коэффициент выхода{' '}
                        <b className="tabular-nums">{fmtQty(100 - (p.wastePercent ?? 0))}%</b>
                      </span>
                    )}
                    {(p.usedInDishes.length > 0 || p.usedInPreps.length > 0) && (
                      <span className="text-xs">
                        Используется:{' '}
                        {[...p.usedInDishes.map((d) => d.name), ...p.usedInPreps.map((x) => x.name)]
                          .slice(0, 4)
                          .join(', ')}
                        {p.usedInDishes.length + p.usedInPreps.length > 4 && ' …'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-outline shrink-0"
                  onClick={() => setEditing({ id: p.id, name: p.name })}
                >
                  <Edit size={14} /> Состав
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <PrepRecipeModal
          prepId={editing.id}
          prepName={editing.name}
          onClose={() => setEditing(null)}
          onSaved={invalidate}
        />
      )}

      {converting && (
        <NewPrepModal
          goods={goods}
          onClose={() => setConverting(false)}
          onCreated={(id, name) => {
            setConverting(false);
            invalidate();
            setEditing({ id, name });
          }}
        />
      )}
    </>
  );
}

/** Новая заготовка: либо с нуля, либо из существующей позиции склада. */
function NewPrepModal({
  goods, onClose, onCreated,
}: {
  goods: StockItem[];
  onClose: () => void;
  onCreated: (id: string, name: string) => void;
}) {
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('кг');
  const [q, setQ] = useState('');

  const createMut = useMutation({
    mutationFn: async () => {
      const item = await createStock({ name: name.trim(), unit });
      await setStockItemKind(item.id, 'PREP');
      return item;
    },
    onSuccess: (item) => onCreated(item.id, item.name),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось создать'),
  });

  const convertMut = useMutation({
    mutationFn: async (item: StockItem) => {
      await setStockItemKind(item.id, 'PREP');
      return item;
    },
    onSuccess: (item) => onCreated(item.id, item.name),
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось'),
  });

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return query ? goods.filter((g) => g.name.toLowerCase().includes(query)) : goods.slice(0, 50);
  }, [goods, q]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Новая заготовка"
      size="sm"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          {mode === 'new' && (
            <button
              className="btn-primary"
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending || name.trim().length === 0}
            >
              Создать и собрать состав
            </button>
          )}
        </>
      }
    >
      <div className="grid gap-3">
        <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
          <FilterBtn active={mode === 'new'} onClick={() => setMode('new')}>Новая позиция</FilterBtn>
          <FilterBtn active={mode === 'existing'} onClick={() => setMode('existing')}>
            Из существующей
          </FilterBtn>
        </div>

        {mode === 'new' ? (
          <>
            <div>
              <label className="label">Название</label>
              <input
                className="input"
                placeholder="Ореховый соус"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="label">Единица учёта</label>
              <select className="input" value={unit} onChange={(e) => setUnit(e.target.value)}>
                {CANONICAL_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <p className="text-xs text-slate-400">
              Цену задавать не нужно — она посчитается из состава на следующем шаге.
            </p>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">
              Если заготовку уже завели как обычный товар (например, «Ореховый соус» с ценой
              вручную) — переведите её здесь, и цена начнёт считаться из состава.
            </p>
            <input
              className="input"
              placeholder="Поиск позиции…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
            <div className="max-h-72 overflow-auto divide-y divide-slate-100 border border-slate-200 rounded-md">
              {filtered.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex justify-between gap-2"
                  onClick={() => convertMut.mutate(g)}
                  disabled={convertMut.isPending}
                >
                  <span className="truncate">{g.name}</span>
                  <span className="text-xs text-slate-400 shrink-0">
                    {formatRub(g.costPerUnit)}/{g.unit}
                  </span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-sm text-slate-400 text-center">Ничего не найдено</div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Инвентаризация ──────────────────────────────────────────────────
function InventoryTab() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const { data: acts, isLoading } = useQuery({
    queryKey: ['admin/stock/inventory'],
    queryFn: listInventoryActs,
  });

  const commitMut = useMutation({
    mutationFn: commitInventoryAct,
    onSuccess: () => {
      toast.success('Акт проведён, остатки обновлены');
      qc.invalidateQueries({ queryKey: ['admin/stock/inventory'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/items'] });
      qc.invalidateQueries({ queryKey: ['admin/stock/movements'] });
    },
  });

  const delMut = useMutation({
    mutationFn: deleteInventoryAct,
    onSuccess: () => {
      toast.success('Удалено');
      qc.invalidateQueries({ queryKey: ['admin/stock/inventory'] });
    },
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">
          Создайте акт инвентаризации, внесите фактические остатки и проведите — разницы спишутся/поступят автоматически.
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>
          <Plus size={16} /> Новая инвентаризация
        </button>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[780px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">№</th>
                <th className="text-left px-4 py-3">Создан</th>
                <th className="text-left px-4 py-3">Статус</th>
                <th className="text-right px-4 py-3">Позиций</th>
                <th className="text-right px-4 py-3">Разница</th>
                <th className="text-left px-4 py-3">Комментарий</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {acts?.map((a) => (
                <tr key={a.id} className="table-row">
                  <td className="px-4 py-3 font-medium">#{a.number}</td>
                  <td className="px-4 py-3">{formatDate(a.createdAt)}</td>
                  <td className="px-4 py-3">
                    {a.status === 'DRAFT'
                      ? <Badge tone="warning">Черновик</Badge>
                      : <Badge tone="success">Проведён</Badge>}
                  </td>
                  <td className="px-4 py-3 text-right">{a.itemsCount}</td>
                  <td className={`px-4 py-3 text-right font-medium ${a.totalDeltaCost < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                    {formatRub(Math.round(a.totalDeltaCost))}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{a.comment ?? '—'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {a.status === 'DRAFT' && (
                      <>
                        <button
                          className="btn-ghost text-emerald-700"
                          disabled={commitMut.isPending}
                          onClick={() => { if (confirm(`Провести акт #${a.number}? После этого он необратим.`)) commitMut.mutate(a.id); }}
                          title="Провести"
                        >
                          <CheckCircle2 size={18} />
                        </button>
                        <button
                          className="btn-ghost text-red-600"
                          onClick={() => { if (confirm(`Удалить акт #${a.number}?`)) delMut.mutate(a.id); }}
                          title="Удалить"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {acts?.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 py-8">Нет актов</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <NewInventoryModal onClose={() => setShowNew(false)} />}
    </>
  );
}

function NewInventoryModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: items } = useQuery({ queryKey: ['admin/stock/items-all'], queryFn: () => listStock() });
  const [comment, setComment] = useState('');
  const [actuals, setActuals] = useState<Record<string, string>>({});

  const createMut = useMutation({
    mutationFn: (args: { comment?: string; items: Array<{ stockItemId: string; actual: number }> }) =>
      createInventoryAct(args),
    onSuccess: () => {
      toast.success('Акт создан как черновик');
      qc.invalidateQueries({ queryKey: ['admin/stock/inventory'] });
      onClose();
    },
  });

  const entries = (items ?? []).map((i) => {
    const raw = actuals[i.id];
    const actual = raw === undefined ? i.quantity : Number(raw);
    const delta = Number.isFinite(actual) ? actual - i.quantity : 0;
    return { item: i, raw, actual, delta };
  });

  const filled = entries.filter((e) => e.raw !== undefined && Number.isFinite(e.actual));
  const canSubmit = filled.length > 0;

  const submit = () => {
    createMut.mutate({
      comment: comment.trim() || undefined,
      items: filled.map((e) => ({ stockItemId: e.item.id, actual: e.actual })),
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Новая инвентаризация"
      size="lg"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn-primary"
            disabled={!canSubmit || createMut.isPending}
            onClick={submit}
          >
            Создать черновик
          </button>
        </>
      }
    >
      <div className="mb-4">
        <label className="label">Комментарий</label>
        <input className="input" value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Например: Инвентаризация на конец смены" />
      </div>
      <div className="overflow-auto max-h-[60vh] border border-slate-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50">
            <tr className="text-slate-500 text-xs uppercase">
              <th className="text-left px-3 py-2">Позиция</th>
              <th className="text-right px-3 py-2">В системе</th>
              <th className="text-right px-3 py-2">Факт</th>
              <th className="text-right px-3 py-2">Разница</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(({ item, raw, delta }) => (
              <tr key={item.id} className="border-t border-slate-100">
                <td className="px-3 py-2">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-xs text-slate-400">{item.unit}</div>
                </td>
                <td className="px-3 py-2 text-right">{item.quantity}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    step="0.01"
                    className="input !py-1 w-28 text-right"
                    value={raw ?? ''}
                    placeholder={String(item.quantity)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setActuals((s) => ({ ...s, [item.id]: v }));
                    }}
                  />
                </td>
                <td className={`px-3 py-2 text-right font-medium ${
                  raw === undefined || raw === ''
                    ? 'text-slate-400'
                    : delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-600' : ''
                }`}>
                  {raw === undefined || raw === '' ? '—' : (delta > 0 ? `+${delta}` : delta)}
                </td>
              </tr>
            ))}
            {(!items || items.length === 0) && (
              <tr><td colSpan={4} className="text-center text-slate-400 py-8">На складе пока нет позиций</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 mt-3">
        Оставьте поле «Факт» пустым — позиция в акт не попадёт.
      </div>
    </Modal>
  );
}

// ─── Поставки (приёмка + оплата из кассы) ─────────────────────────────
// Накладная двигает и остатки, и кассу смены — после любой правки перечитываем
// всё, что от неё зависит.
function invalidateSupplyQueries(qc: ReturnType<typeof useQueryClient>) {
  for (const key of [
    'admin/stock/items',
    'admin/stock/items-all',
    'admin/stock/movements',
    'admin/stock/supplies',
    'admin/stock/supplies-archived-count',
    'admin/current-shift',
  ]) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

function SupplyItemsList({ items }: { items: SupplyItem[] }) {
  return (
    <div className="grid gap-1.5">
      {items.map((it) => (
        <div key={it.id} className="flex items-center justify-between text-sm">
          <span className="text-slate-600">
            {it.name} <span className="text-slate-400">({it.unit})</span>
          </span>
          <span className="tabular-nums text-slate-500">
            {fmtQty(it.quantity)} × {formatRub(it.costPerUnit)} ={' '}
            <b className="text-slate-700">{formatRub(it.lineTotal)}</b>
            {hasPackaging(it) && (
              <span className="text-slate-400"> · {packagingCount(it.quantity, it)}</span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function SuppliesTab() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Supply | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showArchive, setShowArchive] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin/stock/supplies', showArchive],
    queryFn: () => listSupplies({ limit: 200, archived: showArchive || undefined }),
  });

  // Счётчик архива грузим отдельно, чтобы кнопка появлялась и когда открыт
  // список активных накладных.
  const { data: archiveInfo } = useQuery({
    queryKey: ['admin/stock/supplies-archived-count'],
    queryFn: countArchivedSupplies,
  });
  const archivedCount = archiveInfo?.archived ?? 0;

  const delMut = useMutation({
    mutationFn: (id: string) => deleteSupply(id),
    onSuccess: () => {
      toast.success('Поставка удалена, остатки возвращены');
      invalidateSupplyQueries(qc);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Не удалось удалить поставку'),
  });

  // Архивация ничего не двигает на складе и в кассе, поэтому доступна и для
  // запертых накладных — достаточно перечитать списки.
  const archiveMut = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? archiveSupply(id) : unarchiveSupply(id),
    onSuccess: (_res, { archived }) => {
      toast.success(archived ? 'Поставка в архиве' : 'Поставка возвращена в список');
      setExpanded(null);
      invalidateSupplyQueries(qc);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Не удалось изменить архив'),
  });

  const askDelete = (s: Supply) => {
    const what = s.supplierName ? `от «${s.supplierName}»` : `на ${formatRub(s.totalCost)}`;
    const cash =
      s.paymentSource === 'KASSA_CASH'
        ? `\nВыплата ${formatRub(s.paidAmount)} вернётся в кассу смены.`
        : '';
    if (
      confirm(
        `Удалить поставку ${what}?\nСо склада спишется принятое количество (${s.itemsCount} поз.).${cash}`,
      )
    ) {
      delMut.mutate(s.id);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="text-sm text-slate-500">
          {showArchive
            ? 'Архивные накладные скрыты из основного списка. Остатки и выплаты по ним остаются в силе.'
            : 'Примите товар на склад одной накладной и при необходимости оплатите из кассы смены (наличные или перевод).'}
        </div>
        <div className="flex items-center gap-2">
          {(archivedCount > 0 || showArchive) && (
            <button
              className="btn-outline"
              onClick={() => {
                setShowArchive((v) => !v);
                setExpanded(null);
              }}
            >
              {showArchive ? (
                <><History size={16} /> К активным</>
              ) : (
                <><Archive size={16} /> Архив · {archivedCount}</>
              )}
            </button>
          )}
          <button className="btn-primary" onClick={() => setShowNew(true)}>
            <Plus size={16} /> Новая поставка
          </button>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {isLoading ? <Spinner /> : (
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Дата</th>
                <th className="text-left px-4 py-3">Поставщик</th>
                <th className="text-right px-4 py-3">Позиций</th>
                <th className="text-right px-4 py-3">Сумма</th>
                <th className="text-left px-4 py-3">Оплата</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data?.map((s) => (
                <Fragment key={s.id}>
                  <tr
                    className={`table-row cursor-pointer${s.archivedAt ? ' text-slate-400' : ''}`}
                    onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                  >
                    <td className="px-4 py-3">{formatDate(s.createdAt)}</td>
                    <td className="px-4 py-3 font-medium">{s.supplierName ?? '—'}</td>
                    <td className="px-4 py-3 text-right">{s.itemsCount}</td>
                    <td className="px-4 py-3 text-right font-medium">{formatRub(s.totalCost)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={paymentSourceTone[s.paymentSource]}>
                        {paymentSourceLabels[s.paymentSource]}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-slate-400 text-xs mr-1">
                          {expanded === s.id ? 'Скрыть' : 'Состав'}
                        </span>
                        {s.locked && (
                          <span className="text-slate-400 px-1" title={s.lockedReason ?? undefined}>
                            <Lock size={13} />
                          </span>
                        )}
                        {!s.archivedAt && (
                          <button
                            className="btn-ghost p-1.5"
                            title={
                              s.locked
                                ? `${s.lockedReason}. Доступны поставщик и комментарий`
                                : 'Изменить'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing(s);
                            }}
                          >
                            <Edit size={15} />
                          </button>
                        )}
                        <button
                          className="btn-ghost p-1.5 disabled:opacity-30"
                          title={
                            s.archivedAt
                              ? 'Вернуть в список'
                              : 'В архив — скрыть из списка, остатки и оплата останутся'
                          }
                          disabled={archiveMut.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveMut.mutate({ id: s.id, archived: !s.archivedAt });
                          }}
                        >
                          {s.archivedAt ? <ArchiveRestore size={15} /> : <Archive size={15} />}
                        </button>
                        <button
                          className="btn-ghost p-1.5 text-rose-600 disabled:opacity-30"
                          title={s.locked ? (s.lockedReason ?? '') : 'Удалить'}
                          disabled={s.locked || delMut.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            askDelete(s);
                          }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === s.id && (
                    <tr className="bg-slate-50/60">
                      <td colSpan={6} className="px-4 py-3">
                        <SupplyItemsList items={s.items} />
                        {s.comment && (
                          <div className="text-xs text-slate-400 mt-1.5">Комментарий: {s.comment}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {data?.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-slate-400 py-8">
                    {showArchive ? 'В архиве пусто' : 'Пока нет поставок'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showNew && <SupplyModal onClose={() => setShowNew(false)} />}
      {editing && (
        <SupplyModal
          key={editing.id}
          supply={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

// Строка накладной. Количество и цена хранятся в единицах ввода строки
// (inPacks → упаковки), в API уходят в базовых — как и везде на складе.
type SupplyRow = {
  key: string;
  mode: 'existing' | 'new';
  stockItemId: string;
  newName: string;
  newUnit: string;
  newPackLabel: string;
  newPackSize: number;
  inPacks: boolean;
  quantity: number;
  costPerUnitRub: number;
};

const NEW_ITEM_OPTION = '__new__';
const newSupplyRow = (): SupplyRow => ({
  key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  mode: 'existing',
  stockItemId: '',
  newName: '',
  newUnit: 'шт',
  newPackLabel: '',
  newPackSize: 0,
  // Товар привозят упаковками — если у позиции есть фасовка, строка сразу в них.
  inPacks: true,
  quantity: 1,
  costPerUnitRub: 0,
});
const rowIsFilled = (r: SupplyRow) =>
  r.mode === 'new' ? r.newName.trim().length > 0 : !!r.stockItemId;
// Обратная сборка строки из проведённой позиции: в API количество и цена лежат
// в базовых единицах, а форма показывает их в упаковках, когда фасовка задана.
const rowFromSupplyItem = (it: SupplyItem): SupplyRow => {
  const packSize = hasPackaging(it) ? it.packSize : 0;
  return {
    ...newSupplyRow(),
    key: it.id,
    mode: 'existing',
    stockItemId: it.stockItemId,
    inPacks: packSize > 0,
    quantity: packSize > 0 ? baseToPacks(it.quantity, packSize) : it.quantity,
    costPerUnitRub: roundQty((it.costPerUnit * (packSize > 0 ? packSize : 1)) / 100),
  };
};

// Создание и правка накладной одной формой. У запертого документа (оплачен из
// закрытой смены либо принят из «Закупа») состав только для чтения — редактируются
// поставщик и комментарий.
function SupplyModal({ supply, onClose }: { supply?: Supply; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: items } = useQuery({ queryKey: ['admin/stock/items-all'], queryFn: () => listStock() });
  const { data: current } = useQuery({ queryKey: ['admin/current-shift'], queryFn: getCurrentShift });

  const editing = !!supply;
  const locked = !!supply?.locked;
  const [supplierName, setSupplierName] = useState(supply?.supplierName ?? '');
  const [comment, setComment] = useState(supply?.comment ?? '');
  const [paymentSource, setPaymentSource] = useState<SupplyPaymentSource>(
    supply?.paymentSource ?? 'NONE',
  );
  const [rows, setRows] = useState<SupplyRow[]>(() =>
    supply ? supply.items.map(rowFromSupplyItem) : [newSupplyRow()],
  );

  const hasOpenShift = !!current?.shift;
  // Смена нужна, только когда выплату придётся положить в новую: у уже
  // привязанной накладной деньги лежат в своей смене и её не переспрашиваем.
  const needsShift = paymentSource === 'KASSA_CASH' && !supply?.shiftId;

  const usedIds = new Set(rows.filter((r) => r.mode === 'existing').map((r) => r.stockItemId).filter(Boolean));

  const addRow = () => setRows([...rows, newSupplyRow()]);
  const updateRow = (key: string, patch: Partial<SupplyRow>) =>
    setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRow = (key: string) => setRows(rows.filter((r) => r.key !== key));

  // Фасовка строки: у существующей позиции — своя, у новой — та, что вводят рядом.
  const rowPack = (r: SupplyRow) => {
    const p = r.mode === 'new'
      ? { packLabel: r.newPackLabel.trim(), packSize: r.newPackSize, unit: r.newUnit.trim() || 'шт' }
      : (() => {
          const it = items?.find((i) => i.id === r.stockItemId);
          return it ? { packLabel: it.packLabel ?? '', packSize: it.packSize ?? 0, unit: it.unit } : null;
        })();
    return p && hasPackaging(p) ? p : null;
  };
  const rowInPacks = (r: SupplyRow) => r.inPacks && !!rowPack(r);
  const rowFactor = (r: SupplyRow) => rowPack(r)?.packSize ?? 1;
  const rowBaseQty = (r: SupplyRow) => (rowInPacks(r) ? roundQty(r.quantity * rowFactor(r)) : r.quantity);
  const rowBaseCostKop = (r: SupplyRow) =>
    Math.round((r.costPerUnitRub * 100) / (rowInPacks(r) ? rowFactor(r) : 1));
  // Считаем сумму строки из базовых величин — ровно так её посчитает бэкенд.
  const lineTotalKop = (r: SupplyRow) => Math.round(rowBaseCostKop(r) * rowBaseQty(r));
  const totalKop = rows.reduce((sum, r) => sum + (rowIsFilled(r) ? lineTotalKop(r) : 0), 0);

  const switchRowUnit = (r: SupplyRow, toPacks: boolean) => {
    const pack = rowPack(r);
    if (!pack) return;
    const k = pack.packSize;
    updateRow(r.key, toPacks
      ? { inPacks: true, quantity: baseToPacks(r.quantity, k), costPerUnitRub: roundQty(r.costPerUnitRub * k) }
      : { inPacks: false, quantity: packsToBase(r.quantity, k), costPerUnitRub: roundQty(r.costPerUnitRub / k) });
  };

  const errors = useMemo(() => {
    if (locked) return [];
    const e: string[] = [];
    const valid = rows.filter(rowIsFilled);
    if (valid.length === 0) e.push('Добавьте хотя бы одну позицию');
    if (valid.some((r) => !(r.quantity > 0))) e.push('Количество должно быть больше 0');
    if (valid.some((r) => r.costPerUnitRub < 0)) e.push('Цена не может быть отрицательной');
    const counts = new Map<string, number>();
    for (const r of valid.filter((r) => r.mode === 'existing')) {
      counts.set(r.stockItemId, (counts.get(r.stockItemId) ?? 0) + 1);
    }
    if ([...counts.values()].some((n) => n > 1)) e.push('Одна позиция указана несколько раз');
    if (needsShift && !hasOpenShift) e.push('Нет открытой смены — оплата из кассы недоступна');
    return e;
  }, [locked, rows, needsShift, hasOpenShift]);

  // Запертому документу показываем только его фактический источник. У старых
  // накладных источником мог быть перевод из кассы — его нет среди опций
  // создания, но терять его при правке нельзя.
  const paymentOptions = locked
    ? [paymentSource]
    : SUPPLY_PAYMENT_OPTIONS.includes(paymentSource)
      ? SUPPLY_PAYMENT_OPTIONS
      : [paymentSource, ...SUPPLY_PAYMENT_OPTIONS];

  const buildItems = () =>
    rows
      .filter(rowIsFilled)
      .map((r) =>
        r.mode === 'new'
          ? {
              name: r.newName.trim(),
              unit: r.newUnit.trim() || 'шт',
              packLabel: r.newPackLabel.trim() || null,
              packSize: r.newPackSize > 0 ? r.newPackSize : null,
              quantity: rowBaseQty(r),
              costPerUnit: rowBaseCostKop(r),
            }
          : {
              stockItemId: r.stockItemId,
              quantity: rowBaseQty(r),
              costPerUnit: rowBaseCostKop(r),
            },
      );

  const saveMut = useMutation({
    mutationFn: () => {
      if (!supply) {
        return createSupply({
          supplierName: supplierName.trim() || undefined,
          comment: comment.trim() || undefined,
          paymentSource,
          items: buildItems(),
        });
      }
      const labels = {
        supplierName: supplierName.trim() || null,
        comment: comment.trim() || null,
      };
      // Запертому документу отправляем только подписи — состав и оплату
      // бэкенд всё равно отклонит.
      return updateSupply(
        supply.id,
        locked ? labels : { ...labels, paymentSource, items: buildItems() },
      );
    },
    onSuccess: () => {
      toast.success(editing ? 'Поставка изменена' : 'Поставка проведена');
      invalidateSupplyQueries(qc);
      onClose();
    },
    onError: (err) =>
      toast.error(
        err instanceof Error
          ? err.message
          : editing
            ? 'Не удалось изменить поставку'
            : 'Не удалось провести поставку',
      ),
  });

  const submit = () => {
    if (errors.length) { toast.error(errors[0]); return; }
    saveMut.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Поставка' : 'Новая поставка'}
      size="lg"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Отмена</button>
          <button
            className="btn-primary"
            disabled={errors.length > 0 || saveMut.isPending}
            onClick={submit}
          >
            {saveMut.isPending
              ? (editing ? 'Сохраняем…' : 'Проводим…')
              : (editing ? 'Сохранить' : 'Провести поставку')}
          </button>
        </>
      }
    >
      <div className="grid gap-4">
        {locked && (
          <div className="flex gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            <Lock size={14} className="mt-0.5 shrink-0" />
            <span>{supply?.lockedReason}. Можно изменить только поставщика и комментарий.</span>
          </div>
        )}
        {editing && !locked && (
          <div className="text-xs text-slate-500">
            При сохранении прежний приход отменяется и накладная проводится заново — остатки,
            себестоимость и выплата из кассы пересчитаются.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Поставщик</label>
            <input
              className="input"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="Напр.: ООО Фрукты"
            />
          </div>
          <div>
            <label className="label">Комментарий</label>
            <input
              className="input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Необязательно"
            />
          </div>
        </div>

        <div className="grid gap-2">
          <div className="grid grid-cols-[1fr_116px_120px_110px_40px] gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
            <div>Позиция</div>
            <div className="text-right">Кол-во</div>
            <div className="text-right">Цена ₽</div>
            <div className="text-right">Сумма</div>
            <div />
          </div>
          {rows.map((row) => {
            const currentItem = items?.find((i) => i.id === row.stockItemId);
            const availableItems = (items ?? []).filter(
              (i) => i.id === row.stockItemId || !usedIds.has(i.id),
            );
            const pack = rowPack(row);
            const inPacks = rowInPacks(row);
            return (
              <div key={row.key} className="grid grid-cols-[1fr_116px_120px_110px_40px] gap-2 items-center">
                {row.mode === 'new' ? (
                  <div className="flex gap-1.5">
                    <input
                      className="input flex-1"
                      placeholder="Название товара"
                      value={row.newName}
                      autoFocus
                      disabled={locked}
                      onChange={(e) => updateRow(row.key, { newName: e.target.value })}
                    />
                    <input
                      className="input w-16 text-center"
                      placeholder="ед."
                      title="Единица измерения"
                      value={row.newUnit}
                      disabled={locked}
                      onChange={(e) => updateRow(row.key, { newUnit: e.target.value })}
                    />
                  </div>
                ) : (
                  <select
                    className="input"
                    value={row.stockItemId}
                    disabled={locked}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === NEW_ITEM_OPTION) updateRow(row.key, { mode: 'new', stockItemId: '' });
                      else updateRow(row.key, { stockItemId: v });
                    }}
                  >
                    <option value="">— выберите —</option>
                    <option value={NEW_ITEM_OPTION}>➕ Новый товар…</option>
                    {availableItems.map((i) => (
                      <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
                    ))}
                  </select>
                )}
                <div className="flex gap-1">
                  <NumberInput
                    className="input text-right tabular-nums flex-1 min-w-0"
                    value={row.quantity}
                    disabled={locked}
                    onChange={(n) => updateRow(row.key, { quantity: n })}
                  />
                  {pack && (
                    <select
                      className="input w-[62px] shrink-0 px-1 text-[11px]"
                      value={inPacks ? 'pack' : 'base'}
                      title={packagingLabel(pack) ?? ''}
                      disabled={locked}
                      onChange={(e) => switchRowUnit(row, e.target.value === 'pack')}
                    >
                      <option value="pack">{shortPack(pack.packLabel)}</option>
                      <option value="base">{pack.unit}</option>
                    </select>
                  )}
                </div>
                <NumberInput
                  className="input text-right tabular-nums"
                  value={row.costPerUnitRub}
                  disabled={locked}
                  onChange={(n) => updateRow(row.key, { costPerUnitRub: n })}
                />
                <div className="text-right text-sm tabular-nums text-slate-600">
                  {rowIsFilled(row) ? formatRub(lineTotalKop(row)) : '—'}
                </div>
                <button
                  type="button"
                  className="h-10 w-10 flex items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 transition-colors disabled:opacity-30"
                  onClick={() => removeRow(row.key)}
                  disabled={locked || rows.length <= 1}
                  title="Убрать"
                >
                  <Trash2 size={16} />
                </button>
                <div className="col-span-5 text-xs text-slate-400 -mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {row.mode === 'new' ? (
                    <>
                      <span className="text-emerald-600">Новый товар — будет создан на складе</span>
                      <button
                        type="button"
                        className="text-brand-600 hover:underline disabled:opacity-40 disabled:no-underline"
                        disabled={locked}
                        onClick={() => updateRow(row.key, { mode: 'existing', newName: '' })}
                      >
                        выбрать из списка
                      </button>
                      <span className="inline-flex items-center gap-1">
                        фасовка:
                        <select
                          className="input h-7 py-0 px-2 text-xs w-28 text-slate-700"
                          value={row.newPackLabel}
                          disabled={locked}
                          onChange={(e) => updateRow(row.key, {
                            newPackLabel: e.target.value,
                            newPackSize: e.target.value ? row.newPackSize || 1 : 0,
                          })}
                        >
                          <option value="">нет</option>
                          {PACK_PRESETS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        {!!row.newPackLabel && (
                          <>
                            по
                            <NumberInput
                              className="input h-7 py-0 px-2 text-xs w-14 text-right tabular-nums text-slate-700"
                              value={row.newPackSize}
                              disabled={locked}
                              onChange={(n) => updateRow(row.key, { newPackSize: n })}
                            />
                            {row.newUnit.trim() || 'ед.'}
                          </>
                        )}
                      </span>
                    </>
                  ) : currentItem ? (
                    <span>На складе: {fmtQty(currentItem.quantity)} {currentItem.unit}</span>
                  ) : null}
                  {inPacks && pack && row.quantity > 0 && (
                    <span className="text-slate-500">
                      {packagingMath(row.quantity, pack)}
                      {row.costPerUnitRub > 0 && <> · {formatRub(rowBaseCostKop(row))}/{pack.unit}</>}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {!locked && (
            <button type="button" className="btn-outline self-start" onClick={addRow}>
              <Plus size={14} /> Добавить позицию
            </button>
          )}
        </div>

        {/* Блок оплаты */}
        <div className="border-t border-slate-200 pt-4 grid gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Wallet size={16} /> Оплата
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {paymentOptions.map((src) => (
              <button
                key={src}
                type="button"
                disabled={locked}
                onClick={() => setPaymentSource(src)}
                className={`px-3 py-2 rounded-md text-sm border transition-colors disabled:cursor-default ${
                  paymentSource === src
                    ? 'bg-brand-600 text-white border-brand-600'
                    : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {paymentSourceLabels[src]}
              </button>
            ))}
          </div>
          {needsShift && (
            hasOpenShift ? (
              <div className="text-xs text-emerald-700 flex items-center gap-1.5">
                <CheckCircle2 size={14} />
                Смена №{current?.shift?.number} открыта — выплата {formatRub(totalKop)} спишется из кассы.
              </div>
            ) : (
              <div className="text-xs text-rose-600">
                Нет открытой смены. Откройте смену в кассе или выберите другой источник оплаты.
              </div>
            )
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 pt-3">
          <span className="text-sm text-slate-500">Итого к оплате</span>
          <span className="text-lg font-semibold tabular-nums">{formatRub(totalKop)}</span>
        </div>

        {errors.length > 0 && (
          <ul className="text-sm text-rose-600 list-disc pl-5">
            {errors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        )}
      </div>
    </Modal>
  );
}
